/** What one clicked object IS — its identity, its shape, where it stands, and
 *  the rule it answers to.
 *
 *  A generated plan is an argument, and until now the drawing was the whole of
 *  it: a stall was a white rectangle with a car in it and there was no way to
 *  ask why it is 2.50 m wide, which norm sets that, whether this particular one
 *  is accessible, or how far its occupant walks to a stair. Everything needed to
 *  answer was already on the wire and was thrown away at the renderer.
 *
 *  Three pieces live here:
 *
 *    `Selection`  — WHICH object, as indices into the plan the level already
 *                   holds. Deliberately not a copy of the object: the streaming
 *                   solve replaces the plan every 800 ms, and a selection
 *                   holding stale geometry would draw a stall that is no longer
 *                   there. Indices are per level, so `level` rides along.
 *
 *    `RULES`      — the norms, written down once, with the number each one
 *                   fixes. These were scattered through Python comments and
 *                   `ui/src/rules.ts` held a stale duplicate of two of them (ADA
 *                   minimum 1 against the law's 2). One list, quoting the law.
 *
 *    `describeSelection()`
 *               — selection + plan -> the rows the panel prints. Pure, so it
 *                   is testable without a DOM.
 */

import type { SolvedPlan, SolverRoom, SiteLayer, AdaAisle } from "./client";
import type {
  ApartmentLevel, ApartmentFlat, ApartmentRoom, ApartmentCorePart,
} from "./plan/ApartmentView";
import type { Column, FireWall, FireDoor } from "./derive";
import type {
  AlaProps, CanonicalDoc, EvacDriver, PanouFatada, ParapetFatada, StoreyPlan, StoreyShape,
} from "./canonical";
import { numeleFelului } from "./vocabular";
import { detaliiElement } from "./fatada/detalii";
import { bcp47, esteCheie, limba, numar, t } from "../i18n";
import type { Cheie } from "../i18n";

// --------------------------------------------------------------------------
// WHICH object
// --------------------------------------------------------------------------

/** `level` is the numbered storey the indices belong to, or "site" for the
 *  on-grade layer. `i` indexes the level's own list; `j` is the sub-object
 *  (a room inside a flat, a part inside a core) where there is one.
 *
 *  `storey`: PLANȘEUL pe care s-a apăsat, la selecțiile de parcaj. Cu mai multe
 *  săpături la aceeași cotă («toate corpurile» pe un ansamblu cu trei
 *  subsoluri) nivelul și indexul nu ajung: locul `i` există pe fiecare planșeu,
 *  iar rezolvarea pe primul planșeu de la cotă dădea locul — și ruta de
 *  evacuare — altui subsol (c7f3a417, Oana, 26.10.2026). `planulSelectiei`. */
export type Selection =
  // parking levels
  | { kind: "stall"; level: number | "site"; i: number; storey?: string }
  | { kind: "adaAisle"; level: number; i: number; storey?: string }
  | { kind: "column"; level: number; i: number; storey?: string }
  | { kind: "lane"; level: number; i: number; storey?: string }
  | { kind: "room"; level: number; i: number; storey?: string }
  | { kind: "fireWall"; level: number; i: number; storey?: string }
  | { kind: "fireDoor"; level: number; i: number; storey?: string }
  | { kind: "rampInt"; level: number; i: number; storey?: string }
  // floors above ground
  | { kind: "flat"; level: number; i: number }
  | { kind: "flatRoom"; level: number; i: number; j: number }
  | { kind: "balcony"; level: number; i: number }
  | { kind: "corePart"; level: number; i: number; j: number }
  | { kind: "bar"; level: number; i: number }
  | { kind: "corridor"; level: number; i: number }
  | { kind: "building"; level: number; i: number }
  // the site layer
  | { kind: "siteBar"; level: "site"; i: number }
  | { kind: "siteEntrance"; level: "site"; i: number }
  // O ALEE a planului de situație (`Road`), pe indexul ei în `site.roads`:
  // ca omul să poată spune «aleea T3» când dă feedback pe circulație.
  | { kind: "siteRoad"; level: "site"; i: number }
  // O CASĂ DE SCARĂ DE EVACUARE a parcajului, la sol, pe indexul ei în `site.scariEvacuare`.
  | { kind: "siteStair"; level: "site"; i: number }
  // UN TROTUAR, O TRECERE DE PIETONI, O ZONĂ EXTERIOARĂ ale planului de situație
  // (3.18), pe indexul lor în `site.sidewalks` / `site.crossings` / `site.zones`:
  // omul le apasă și le citește id-ul agentului («T12», «X2»), ca să le poată
  // numi în discuția cu asistentul (Oana, 30.10.2026).
  | { kind: "siteSidewalk"; level: "site"; i: number }
  | { kind: "siteCrossing"; level: "site"; i: number }
  | { kind: "siteZone"; level: "site"; i: number }
  // A WHOLE STOREY, with nothing highlighted inside it.
  //
  // Added for the validator, and it is the honest ref for a rule whose subject is
  // the floor rather than a thing on it: the number of simultaneous users of a
  // parking level (15% of the bays, two persons each), the clear height of the
  // storey, the conflict points of its circulation. Those had no navigable
  // reference before, and the alternatives were both lies — point at bay #0, or
  // emit one identical row per bay.
  //
  // `i` is absent on purpose: there is exactly one storey per level, so an index
  // would be a second way to say the same thing.
  | { kind: "storey"; level: number | "site" }
  // UN OBIECT AL NIVELULUI DE ARHITECTURĂ, pe identitatea lui din document.
  //
  // Singura selecție care nu se ține pe index, și dinadins: nivelul importat nu
  // are liste omogene („al treilea perete" n-are înțeles când peretele, golul
  // și camera sunt trei liste care se redesenează împreună), iar `eid` e exact
  // identitatea la care se referă și validatorul. Vezi `ApartmentRoom.eid`.
  | { kind: "obiect"; level: number; eid: string }
  // O COTĂ a unui nivel de arhitectură (Oana, 20.10.2026): clickul pe cifră o
  // aprinde pe desen, ca să se vadă ce măsoară numărul. `eid` e «strat:index»
  // din `coteNivel` (`ala:12`), nu o identitate din document: cotele se
  // calculează din geometrie și n-au id. Fără panou: n-are ce spune în plus
  // față de cifra de pe desen.
  | { kind: "cota"; level: number; eid: string }
  // ONE SIDE OF THE PROPERTY LINE — a `PlotEdge` object on the site layer.
  //
  // The urbanism rules are counted per side: a setback breached on two of four
  // sides is the answer, and four instances all pointing at the whole site cannot
  // say it. `i` is the side's own `side_index`, so the ref survives the array
  // being reordered.
  | { kind: "plotEdge"; level: "site"; i: number };

export const sameSelection = (a: Selection | null, b: Selection | null) =>
  // `i` and `j` are read optionally: a `storey` selection has neither, because
  // there is exactly one storey per level and an index would be a second way to
  // say the same thing.
  !!a && !!b && a.kind === b.kind && a.level === b.level
  && (a as { i?: number }).i === (b as { i?: number }).i
  && (a as { j?: number }).j === (b as { j?: number }).j
  // `eid` la fel de opțional ca indicii: o selecție de arhitectură se ține pe
  // identitatea obiectului, și fără linia asta două obiecte diferite de pe
  // același nivel ieșeau „aceeași selecție" — al doilea click deselecta.
  && (a as { eid?: string }).eid === (b as { eid?: string }).eid;

/** PLANȘEUL unei selecții, dintre cele desenate: cel al `storey`-ului ei, când
 *  selecția îl poartă; altfel primul de la cota ei. Pe o selecție care nu e a
 *  unui planșeu (nimic, situația), primul planșeu desenat. */
export function planulSelectiei<P extends { storey: string; level: number }>(
  planuri: readonly P[], sel: Selection | null | undefined): P | null {
  const storey = (sel as { storey?: string } | null | undefined)?.storey;
  if (storey) {
    const al = planuri.find((pl) => pl.storey === storey);
    if (al) return al;
  }
  const level = (sel as { level?: number | "site" } | null | undefined)?.level;
  return (typeof level === "number" ? planuri.find((pl) => pl.level === level) : undefined) ?? planuri[0] ?? null;
}

/** How a drawn shape says what it is: a `data-pick` attribute carrying its own
 *  Selection, plus a pointer cursor.
 *
 *  NOT a click handler, and that is the whole design. The first version put
 *  `onClick` on every glyph with a `stopPropagation` on pointerdown beside it —
 *  because `Editor.startPan` calls `setPointerCapture` on the SVG root, which
 *  retargets the pointerup and swallows the click. Stopping the pointerdown
 *  does restore the click, and it also stops `startPan` from ever running: it
 *  killed drag-to-pan everywhere a glyph covers the drawing (which above ground
 *  is the whole building) and the measuring tool with it, since the measure
 *  gesture also begins in `startPan`.
 *
 *  So the events stay on the SVG root, where panning and measuring already
 *  live, and the Editor asks the DOM what was hit when a press turns out not to
 *  have been a drag. `Element.closest` walks UP, so a label, a piece of
 *  furniture or a door leaf painted inside a room resolves to that room, and a
 *  room inside a flat resolves to the room rather than the flat — the nesting
 *  does the disambiguation for free. */
export interface PickAttrs {
  "data-pick"?: string;
  style?: { cursor: "pointer" };
}

export const pickAttrs = (s: Selection | null | undefined): PickAttrs =>
  s ? { "data-pick": JSON.stringify(s), style: { cursor: "pointer" } } : {};

/** The innermost drawn object at `el`, or null for bare canvas. */
export function selectionFromTarget(el: EventTarget | null): Selection | null {
  const hit = el instanceof Element ? el.closest("[data-pick]") : null;
  const raw = hit?.getAttribute("data-pick");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Selection;
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------
// The rules, quoted
// --------------------------------------------------------------------------

/** solver.EVAC_TWO_DIR_M. Named rather than inlined because it is BOTH the
 *  required value the panel prints and the threshold it flags against, and the
 *  two must not be able to drift apart. */
const EVAC_TWO_DIR_M = 42;

/** One norm and the number it fixes. `value` is what the rule REQUIRES; the
 *  panel prints the object's own measurement beside it, so a piece that drifts
 *  from the rule it claims to satisfy shows up as two different numbers rather
 *  than as a reassuring label. */
export interface Rule {
  id: string;
  /** Indicativul actului — nume propriu, nu se traduce. */
  norm: string;
  /** Ce cere, în limba ecranului. */
  text: string;
  /** Cât cere, când cifra e scurtă și se poate scrie lângă. */
  value?: string;
}

/** Normele, citite din catalog la fiecare chemare — ca limba să fie cea de
 *  ACUM. Un obiect de modul construit o dată ar fi înghețat limba de la
 *  încărcarea paginii. */
export function reguli(): Record<string, Rule> {
  return {
  stallSize: {
    id: "stall.size", norm: "NP 24-97 / STAS 10144",
    text: t("norma.stall.size"), value: t("norma.val.stall_size"),
  },
  aisleWidth: {
    id: "aisle.width", norm: "NP 24-2022 tab. 6.1",
    text: t("norma.aisle.width"), value: t("norma.val.aisle_width"),
  },
  adaCount: {
    id: "ada.count", norm: "Legea 448/2006 art. 65",
    text: t("norma.ada.count"), value: t("norma.val.ada_count"),
  },
  adaStrip: {
    id: "ada.strip", norm: "NP 051-2012",
    text: t("norma.ada.strip"), value: t("norma.val.ada_strip"),
  },
  adaLevel: {
    id: "ada.level", norm: "NP 051-2012",
    text: t("norma.ada.level"),
  },
  fireWall: {
    id: "fire.wall", norm: "P118-99 / P118-2/2013",
    text: t("norma.fire.wall"),
    value: "EI 120",
  },
  fireDoor: {
    id: "fire.door", norm: "P118-99",
    text: t("norma.fire.door"),
    value: "EI 60-c",
  },
  coreEnclosure: {
    id: "fire.core", norm: "P118-99 / NP 127-2009",
    text: t("norma.fire.core"),
    value: "REI 180",
  },
  // 42 / 21, the numbers `solver.EVAC_TWO_DIR_M` and `EVAC_DEAD_END_M`
  // actually enforce and `EvacView` actually colours against. Written here as
  // 40 / 20 first, which is exactly the failure this panel exists to prevent:
  // a plausible round number printed as the requirement, beside a measurement
  // the solver judged against a different one.
  evacTwoWay: {
    id: "evac.two_dir", norm: "P118-99",
    text: t("norma.evac.two_dir"),
    value: "42 m",
  },
  evacDeadEnd: {
    id: "evac.dead_end", norm: "P118-99",
    text: t("norma.evac.dead_end"),
    value: "21 m",
  },
  rampSlope: {
    id: "ramp.slope", norm: "NP 24-97",
    text: t("norma.ramp.slope"),
    value: "18%",
  },
  usefulArea: {
    id: "flat.useful", norm: "Legea 114/1996, anexa 1, tab. B",
    text: t("norma.flat.useful"),
  },
  livingRoom: {
    id: "room.living", norm: "Legea 114/1996, anexa 1, tab. B (R04)",
    text: t("norma.room.living"),
  },
  bedroom: {
    id: "room.bedroom", norm: "Legea 114/1996, anexa 1, tab. B (R05)",
    text: t("norma.room.bedroom"),
  },
  kitchen: {
    id: "room.kitchen", norm: "Legea 114/1996, anexa 1, tab. B (R06)",
    text: t("norma.room.kitchen"),
  },
  sanitary: {
    id: "room.sanitary", norm: "Legea 114/1996, anexa 1, secț. C (R09–R12)",
    text: t("norma.room.sanitary"),
  },
  store: {
    id: "room.store", norm: "Legea 114/1996, anexa 1, tab. B (R08)",
    text: t("norma.room.store"),
  },
  hallWidth: {
    id: "room.hall", norm: "Legea 114/1996 (R13)",
    text: t("norma.room.hall"), value: t("norma.val.hall_width"),
  },
  insolation: {
    id: "flat.insolation", norm: "Ordin MS 119/2014 art. 3",
    text: t("norma.flat.insolation"),
  },
  corridorWidth: {
    id: "bar.corridor", norm: "NP 057-02",
    text: t("norma.bar.corridor"), value: t("norma.val.corridor_width"),
  },
  balcony: {
    id: "flat.balcony", norm: "Legea 114/1996, anexa 1 (R01)",
    text: t("norma.flat.balcony"),
  },
  evacFlat: {
    id: "evac.flat", norm: "P118-99",
    text: t("norma.evac.flat"),
    value: "50 m",
  },
  column: {
    id: "struct.column", norm: t("norma.sursa_proiect"),
    text: t("norma.struct.column"),
  },
  };
}

function roomRule(R: Record<string, Rule>): Record<string, Rule | undefined> {
  return {
  living: R.livingRoom,
  bedroom: R.bedroom,
  kitchen: R.kitchen,
  bath: R.sanitary,
  bath2: R.sanitary,
  wc: R.sanitary,
  shower: R.sanitary,
  store: R.store,
  hall: R.hallWidth,
  };
}

// --------------------------------------------------------------------------
// What the panel prints
// --------------------------------------------------------------------------

/** The outline the panel draws above the numbers.
 *
 *  A number cannot show a notch. "Colțuri: 7 (formă în L)" says the room is not
 *  a rectangle but not WHICH corner was eaten, and on a flat that wrapped a
 *  stair core that is the only thing worth looking at. `context` rings are
 *  drawn faintly behind `poly` — for a room, its siblings in the same dwelling
 *  — because an outline on its own does not say which end of the flat you are
 *  holding. Metre space, same orientation as the drawing: the views apply only
 *  translate/scale (Editor.tsx:876), never a Y flip, so these points can go
 *  into a viewBox unchanged. */
export interface Figure {
  poly: number[][];
  context?: number[][][];
}

export interface Row { k: string; v: string; warn?: boolean }
export interface Group { label: string; rows: Row[] }
export interface Described {
  title: string;
  /** Short tags under the title (type, level, accessible, substituted…). */
  chips: string[];
  groups: Group[];
  rules: Rule[];
  /** Metre-space point the panel can point a leader line at, when there is one. */
  at?: [number, number];
  /** What to draw above the rows, when the object has a shape worth seeing. */
  figure?: Figure;
  /** The object this one sits INSIDE, when there is a bigger thing worth
   *  selecting. Hit-testing resolves to the innermost `data-pick` (see
   *  `selectionFromTarget`), which is right for a press but leaves the whole
   *  dwelling unreachable: a flat is covered by its own rooms, so every click
   *  inside one lands on a room. Rather than fight the nesting with a modifier
   *  key — the gesture budget is already spent on pan and measure — the panel
   *  offers the parent as a link. */
  parent?: { label: string; sel: Selection };
}

// TOTALE, nu parțiale.
//
// Aveau semnătura `(x: number)`, iar cele nouăsprezece locuri care le apelează
// se bazau pe tipuri ca să garanteze că valoarea există. Unul dintre tipuri
// mințea (`ApartmentBuilding.useful_m2`), și atunci un `undefined.toFixed` lua
// TOT ecranul — un panou de detalii care nu poate scrie o arie nu e un motiv să
// dispară planul de sub el. „nedeclarat" e cuvântul folosit deja pentru asta,
// mai jos, la înălțimile de nivel.
// ...ȘI ÎN CONVENȚIA LIMBII. `toFixed` scrie mereu cu punct, iar 2.50 citit de
// cineva obișnuit cu virgula e altă cifră decât 2,50. `numar` ia separatorul
// din limba activă — v. `i18n::numar`.
const doua = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
/** Lungimea unei axe frânte (deja tesselate), în metri. */
const lungimeAxei = (ax: number[][]) => {
  let L = 0;
  for (let k = 0; k + 1 < ax.length; k++) L += Math.hypot(ax[k + 1][0] - ax[k][0], ax[k + 1][1] - ax[k][1]);
  return L;
};

const m = (x?: number | null) =>
  (x == null ? t("insp.nedeclarat") : `${numar(x, doua)} m`);
const m2 = (x?: number | null) =>
  (x == null ? t("insp.nedeclarat") : `${numar(x, doua)} m²`);
const xy = (x: number, y: number) => `${numar(x, doua)}, ${numar(y, doua)}`;
/** Un unghi, cu o zecimală. */
const grade = (d: number) => `${numar(d, { maximumFractionDigits: 1 })}°`;

/** A polygon's area, its oriented extent and its vertex count — the three
 *  things "shape" means for a drawn object. Ring-agnostic so it can take a
 *  flat, a room, a balcony or a site bar. */
/** COLȚURI, nu puncte stocate.
 *
 *  Un inel venit din import poartă un vârf oriunde a avut desenul unul: fiecare
 *  nod de perete, fiecare tocărie, fiecare colț de 1,5 cm al lipiciului cu care
 *  importatorul scoate fața liberă dintre pereți. Un hol de 12,76 m² ajunge așa
 *  la 92 de puncte stocate și vreo 8 colțuri adevărate — iar panoul scria 92,
 *  ceea ce e exact, dar despre altceva decât întreabă omul.
 *
 *  Toleranța e o DISTANȚĂ PERPENDICULARĂ în metri, nu un produs vectorial: acela
 *  e o arie, deci s-ar înăspri pe laturile scurte și s-ar slăbi pe cele lungi —
 *  pe dos față de ce trebuie pe un coridor lung și îngust. */
const EPS_PUNCT_M = 1e-6;
const EPS_COLINIAR_M = 0.005;      // 5 mm: sub atât, vârful e pe latură

function colturi(poly: readonly number[][]): number[][] {
  const p: number[][] = [];
  for (const q of poly) {                       // fără repetări consecutive
    const u = p[p.length - 1];
    if (!u || Math.hypot(q[0] - u[0], q[1] - u[1]) > EPS_PUNCT_M) p.push(q);
  }
  while (p.length > 1                           // fără punctul de închidere
         && Math.hypot(p[0][0] - p[p.length - 1][0],
                       p[0][1] - p[p.length - 1][1]) <= EPS_PUNCT_M) p.pop();
  const out: number[][] = [];
  for (let i = 0; i < p.length; i++) {          // fără puncte coliniare
    const a = p[(i - 1 + p.length) % p.length], b = p[i], c = p[(i + 1) % p.length];
    const L = Math.hypot(c[0] - a[0], c[1] - a[1]);
    const d = Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
    if (!L || d / L > EPS_COLINIAR_M) out.push(b);
  }
  return out.length >= 3 ? out : p;
}

/** Grupul «Evacuare» al unei scări cumpărate de limită, sau nimic. */
/** ADĂPOSTUL ALA, la click: tot ce nu scrie pe planșă (acolo stau doar numele
 *  scurt și aria, ca să nu se aglomereze) — persoanele, suprafața de adăpostire
 *  față de necesar, SAS-ul, grupul sanitar, ieșirile, locurile luate și
 *  neconcordanța, când e una. Pe SAS, grupul sanitar, ieșiri și pereți: al
 *  cărui adăpost sunt și ce rol au. */
function alaGrup(a: AlaProps | undefined): Group[] {
  if (!a) return [];
  const rows: Row[] = [];
  const mp = (x: number) => `${x.toFixed(1)} m²`;
  if (a.adapost) rows.push({ k: t("insp.ala.adapost"), v: a.adapost });
  if (a.persoane != null) rows.push({ k: t("insp.ala.persoane"), v: String(a.persoane) });
  if (a.cladire) rows.push({ k: t("insp.ala.cladire"), v: a.cladire });
  if (a.adapostire_m2 != null) {
    rows.push({ k: t("insp.ala.adapostire"),
                v: mp(a.adapostire_m2) + (a.necesar_m2 != null
                  ? ` · ${t("insp.ala.necesar")} ${mp(a.necesar_m2)}` : "") });
  }
  const d = a.dimensionare;
  if (d) {
    const n = (x: number, z = 2) => x.toFixed(z).replace(".", ",");
    // De unde iese necesarul de mai sus: volumul pe persoană împărțit la
    // înălțimea luată în calcul, înmulțit cu persoanele. Stătea scris pe
    // planșă, sub arie; acolo aglomera camera (Oana, 19.10.2026).
    const p = a.persoane ?? d.capacitate;
    rows.push({ k: t("insp.ala.calcul"),
                v: `${n(d.mc_pe_persoana)} m³ ÷ h ${n(d.h_m)} m = ${n(d.mp_pe_persoana, 3)} m²/pers.`
                   + ` · ${p} pers. → ${t("insp.ala.necesar")} ${n(a.necesar_m2 ?? p * d.mp_pe_persoana, 1)} m²` });
    rows.push({ k: t("insp.ala.h"), v: `${n(d.h_m)} m` });
    rows.push({ k: t("insp.ala.pe_persoana"),
                v: `max(${n(d.mp_pe_persoana_arie)} m²; ${n(d.mc_pe_persoana)} m³ ÷ ${n(d.h_m)} m)`
                   + ` = ${n(d.mp_pe_persoana, 3)} m² · ${t(d.leaga === "volum" ? "insp.ala.leaga_volum" : "insp.ala.leaga_arie")}` });
    rows.push({ k: t("insp.ala.tinta"),
                v: `${d.capacitate} × ${n(d.mp_pe_persoana, 3)} + SAS ${n(d.sas_m2, 1)} + GS ${n(d.grup_sanitar_m2, 1)}`
                   + ` = ${n(d.tinta_m2, 1)} m²` });
  }
  if (a.arie_m2 != null) rows.push({ k: t("insp.ala.arie"), v: mp(a.arie_m2) });
  if (a.bucata) rows.push({ k: t("insp.ala.bucata"), v: a.mod ? `${a.bucata} · ${a.mod}` : a.bucata });
  if (a.sas) rows.push({ k: t("insp.ala.sas"), v: a.sas });
  if (a.grup_sanitar) rows.push({ k: t("insp.ala.grup_sanitar"), v: a.grup_sanitar });
  if (a.cabine != null) rows.push({ k: t("insp.ala.cabine"), v: String(a.cabine) });
  if (a.lavoare != null) rows.push({ k: t("insp.ala.lavoare"), v: String(a.lavoare) });
  (a.iesiri ?? []).forEach((x, i) =>
    rows.push({ k: `${t("insp.ala.iesire")} ${i + 1}`, v: x }));
  if (a.locuri_pierdute != null) {
    rows.push({ k: t("insp.ala.locuri"), v: String(a.locuri_pierdute) });
  }
  if (a.neconcordanta) rows.push({ k: t("insp.ala.neconcordanta"), v: a.neconcordanta, warn: true });
  if (a.persoane == null && a.rol) rows.push({ k: t("insp.ala.rol"), v: a.rol.replace(/_/g, " ") });
  if (a.gol) rows.push({ k: t("insp.ala.gol"), v: a.gol.replace(/_/g, " ") });
  if (a.etans) rows.push({ k: t("insp.ala.perete"), v: t("insp.ala.beton_etans") });
  return rows.length ? [{ label: t("insp.grup.ala"), rows }] : [];
}

function evacuareGrup(d: EvacDriver | undefined): Group[] {
  if (!d) return [];
  const rows: Row[] = [
    { k: t("insp.evac.punct"), v: xy(d.point[0], d.point[1]) },
  ];
  // `walk_m` absent = punctul n-avea NICIUN traseu; a spune „0 m" ar fi invers
  // decât realitatea, iar a tăcea ar ascunde cazul cel mai grav.
  // Pragul cu care se compară e limita PLUS toleranța de măsurare, fiindcă așa
  // compară și solverul (`EVAC_TOL`). Fără ea, o plimbare de 42,2 m ar fi
  // marcată roșu pe un card, deși schema e considerată conformă.
  const prag = d.limit_m == null ? null : d.limit_m + (d.tol_m ?? 0);
  rows.push(d.walk_m == null
    ? { k: t("insp.evac.fara_scara"), v: t("insp.evac.niciun_traseu"), warn: true }
    : { k: t("insp.evac.fara_scara"), v: m(d.walk_m),
        warn: prag != null && d.walk_m > prag });
  if (d.walk_new_m != null) rows.push({ k: t("insp.evac.cu_ea"), v: m(d.walk_new_m) });
  if (d.limit_m != null) rows.push({ k: t("insp.evac.limita"), v: m(d.limit_m) });
  // ...și cu ce toleranță s-a măsurat. Rândul există ca «42,2 contra 42,0» să
  // nu se citească drept încălcare: rasterul de evacuare are pasul de 1,5 m,
  // deci cifra nu poate fi mai exactă de atât.
  if (d.tol_m != null && d.tol_m > 0) {
    rows.push({ k: t("insp.evac.toleranta"), v: `± ${m(d.tol_m)}` });
  }
  return [{ label: t("insp.grup.evacuare"), rows }];
}

function shapeOf(poly: number[][] | undefined, deg = 0): Row[] {
  if (!poly || poly.length < 3) return [];
  // A ring that repeats its first point (shapely's `exterior.coords` does)
  // must not count that repeat as a corner.
  const closed = poly.length > 3
    && poly[0][0] === poly[poly.length - 1][0]
    && poly[0][1] === poly[poly.length - 1][1];
  const n = closed ? poly.length - 1 : poly.length;
  let a = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = poly[i], [x2, y2] = poly[(i + 1) % n];
    a += x1 * y2 - x2 * y1;
  }
  // Measured in the object's OWN frame. A bar can run at any angle and a
  // world-axis bounding box sizes a flat on a skewed run to the wrong edge.
  const r = (deg * Math.PI) / 180, co = Math.cos(r), si = Math.sin(r);
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
  for (const [x, y] of poly) {
    const u = x * co + y * si, v = -x * si + y * co;
    u0 = Math.min(u0, u); u1 = Math.max(u1, u);
    v0 = Math.min(v0, v); v1 = Math.max(v1, v);
  }
  const rows: Row[] = [
    { k: t("insp.rand.arie"), v: m2(Math.abs(a) / 2) },
    { k: t("insp.rand.gabarit"), v: `${m(u1 - u0)} × ${m(v1 - v0)}` },
    // Numele formei se dă doar când se poate DEMONSTRA: patru colțuri e un
    // dreptunghi, șase e un L. Restul rămâne un număr — «formă în L» pus pe
    // ramura „altfel" spunea „nu are patru colțuri", pe orice, inclusiv pe 92.
    { k: t("insp.rand.colturi"), v: (() => {
        const nc = colturi(poly).length;
        const forma = nc === 4 ? t("insp.forma.dreptunghi")
                    : nc === 6 ? t("insp.forma.l") : "";
        return `${nc}${forma}`;
      })() },
  ];
  if (deg) rows.push({ k: t("insp.rand.orientare"), v: grade(deg) });
  return rows;
}

/** The four corners of a `cx/cy/w/h/deg` object, so a stall, a column, a
 *  parking room or the ramp can hand the panel the same thing a polygon object
 *  does. These are the only kinds whose geometry is stored as a centre and a
 *  size rather than as a ring. */
function rectPoly(cx: number, cy: number, w: number, h: number, deg = 0): number[][] {
  const r = (deg * Math.PI) / 180, co = Math.cos(r), si = Math.sin(r);
  const hw = w / 2, hh = h / 2;
  return ([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as number[][])
    .map(([u, v]) => [cx + u * co - v * si, cy + u * si + v * co]);
}

function centroid(poly: number[][]): [number, number] {
  let x = 0, y = 0;
  for (const p of poly) { x += p[0]; y += p[1]; }
  return [x / poly.length, y / poly.length];
}

export interface DescribeCtx {
  plan?: SolvedPlan | null;
  /** The DERIVED columns actually on screen — `plan.columns` before PlanView's
   *  in-room filter, so an index means the same column in both places. */
  columns?: Column[];
  fireWalls?: FireWall[];
  fireDoors?: FireDoor[];
  apt?: ApartmentLevel | null;
  site?: SiteLayer | null;
  /** Nivelul de arhitectură desenat acum — pereți, goluri, camere, structură.
   *  Sursa pentru selecția `obiect`, care se caută pe `eid`, nu pe index. */
  /** Planșeele active — TOATE, nu doar primul: la aceeași cotă stă câte unul de
   *  fiecare corp, iar selecția `obiect` se caută pe `eid` prin toate. */
  storey?: StoreyPlan | readonly StoreyPlan[] | null;
  /** Documentul soluției: balustradele și peretele cortină se descriu din el,
   *  cu aceeași fișă ca pe machetă (`fatada/detalii`). */
  doc?: CanonicalDoc | null;
}

/** Cum se numesc familiile de obiecte ale unui nivel de arhitectură. Felul
 *  (`kind`) vine din vocabularul canonic — v. `vocabular::numeleFelului`;
 *  familia e CLASA lui, adică `type`-ul din document, și pe aia n-o spune
 *  catalogul de feluri. */
const FAMILIE: Record<string, Cheie> = {
  wall: "insp.familie.wall", opening: "insp.familie.opening",
  space: "insp.familie.space", slab: "insp.familie.slab",
  column: "insp.familie.column", beam: "insp.familie.beam",
  flat: "insp.familie.flat", core: "insp.familie.core",
  ladder: "insp.familie.ladder",
};

/** Numele familiei, în limba ecranului; o familie necunoscută rămâne cum e. */
const numeleFamiliei = (f: string) => (FAMILIE[f] ? t(FAMILIE[f]) : f);

/** Conturul apartamentului din care face parte obiectul, plus camerele lui
 *  surori — desenate punctat în spatele obiectului, ca să se vadă unde stă. */
function contextulCamerei(o: StoreyShape, nivel: StoreyPlan): number[][][] | undefined {
  if (!o.flat) return undefined;
  const apt = nivel.flatShapes.find((f) => f.eid === o.flat);
  const surori = nivel.spaces
    .filter((s) => s.flat === o.flat && s.eid !== o.eid && s.poly.length >= 3)
    .map((s) => s.poly);
  const inele = [...(apt && apt.poly.length >= 3 ? [apt.poly] : []), ...surori];
  return inele.length ? inele : undefined;
}

/** Obiectul cu `eid`-ul dat, oriunde ar sta pe nivel, și familia din care face
 *  parte. Căutarea e pe identitate, nu pe index: v. nota de la selecția
 *  `obiect`. */
function gasesteObiect(st: StoreyPlan | readonly StoreyPlan[] | null, eid: string):
    { o: StoreyShape; familie: string; nivel: StoreyPlan } | null {
  if (!st) return null;
  // Se caută pe TOATE planșeele active, nu doar pe primul. La aceeași cotă stau
  // câte un planșeu de fiecare corp — șase, pe un ansamblu — iar contextul purta
  // doar `planseeActive[0]`. Un click pe orice obiect al corpurilor 2…6 dădea un
  // `eid` negăsit, `describeSelection` întorcea `null` și panoul nu se mai
  // deschidea deloc. Adică: cu «Toate corpurile», selecția mergea pe o șesime
  // din desen, fără niciun semn că restul e altceva decât pânză goală.
  for (const nivel of Array.isArray(st) ? st : [st as StoreyPlan]) {
    const familii: [string, StoreyShape[]][] = [
      ["wall", nivel.walls], ["opening", nivel.openings], ["space", nivel.spaces],
      ["slab", nivel.slabs], ["column", nivel.columns], ["beam", nivel.beams],
      // Apartamentele lipseau din listă, deși `StoreyView` le desenează CU
      // `pickAttrs` (conturul lor, StoreyView.tsx:339-344). Clicul pe un
      // apartament trecea deci prin toate familiile, nu găsea nimic și panoul nu
      // se deschidea — iar de pe desen nu se putea vedea de ce: conturul arăta
      // exact ca orice altceva selectabil, cu cursor de mână.
      ["flat", nivel.flatShapes],
      // Părțile nucleului — desenate de `StoreyView` cu `pickAttrs`, ca orice
      // cameră; fără rândul ăsta, clicul pe casa scării nu deschidea panoul.
      ["core", nivel.cores],
      // Scările de pisică ale săriturilor de lup (`Ladder`), desenate cu
      // `pickAttrs` de `StoreyView.ScaraDePisica`; fără rândul ăsta clicul pe
      // ele nu deschidea panoul (Oana, 30.09.2026).
      ["ladder", nivel.ladders ?? []],
    ];
    for (const [familie, lista] of familii) {
      const o = lista.find((x) => x.eid === eid);
      if (o) return { o, familie, nivel };
    }
  }
  return null;
}

/** O piesă a FAȚADEI desenată pe planul nivelului: balustrada unui balcon
 *  (`Railing`) sau peretele cortină (`FacadePanel curtain_wall`). Nu sunt
 *  `StoreyShape`, deci `gasesteObiect` nu le vede. */
function gasestePiesaFatadei(st: StoreyPlan | readonly StoreyPlan[] | null, eid: string):
    { r?: ParapetFatada; p?: PanouFatada; nivel: StoreyPlan } | null {
  if (!st) return null;
  for (const nivel of (Array.isArray(st) ? st : [st]) as readonly StoreyPlan[]) {
    const r = (nivel.parapete ?? []).find((x) => x.eid === eid);
    if (r) return { r, nivel };
    const p = (nivel.panouri ?? []).find((x) => x.eid === eid);
    if (p) return { p, nivel };
  }
  return null;
}

/** Banda din jurul unei polilinii (`± jum` pe normala fiecărui segment): figura
 *  unei balustrade, care n-are contur propriu. */
function bandaAxei(ax: number[][], jum: number): number[][] {
  const st: number[][] = [], dr: number[][] = [];
  ax.forEach((q, i) => {
    const a = ax[Math.max(0, i - 1)], b = ax[Math.min(ax.length - 1, i + 1)];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const nx = -(b[1] - a[1]) / L, ny = (b[0] - a[0]) / L;
    st.push([q[0] + nx * jum, q[1] + ny * jum]);
    dr.push([q[0] - nx * jum, q[1] - ny * jum]);
  });
  return [...st, ...dr.reverse()];
}

/** Balustrada sau cortina aleasă pe plan, cu rândurile fișei de pe machetă. */
function descriePiesaFatadei(g: { r?: ParapetFatada; p?: PanouFatada; nivel: StoreyPlan },
                             eid: string, doc: CanonicalDoc | null | undefined): Described | null {
  const tr = (k: string, rezerva: string) => (esteCheie(k) ? t(k as Cheie) : rezerva);
  const det = detaliiElement(doc ?? null, eid, bcp47(limba()),
    (m) => tr(`material.${m}`, m.replace(/_/g, " ")),
    (gr, v) => tr(`detalii.v.${gr}.${v}`, v.replace(/_/g, " ")));
  const { r, p, nivel } = g;
  const fel = numeleFelului(r ? r.kind : p!.kind) || (r ? r.kind : p!.kind);
  let figura: number[][];
  let at: [number, number];
  if (r) {
    figura = bandaAxei(r.axis, Math.max((r.thickness_m ?? 0.02) / 2, 0.03));
    const m = r.axis[Math.floor(r.axis.length / 2)];
    const q = r.axis[Math.max(0, Math.floor(r.axis.length / 2) - 1)];
    at = [(m[0] + q[0]) / 2, (m[1] + q[1]) / 2];
  } else {
    const n = p!.n ?? [0, 0], d = p!.depth_m;
    figura = [p!.a, p!.b, [p!.b[0] + n[0] * d, p!.b[1] + n[1] * d], [p!.a[0] + n[0] * d, p!.a[1] + n[1] * d]];
    at = [(p!.a[0] + p!.b[0]) / 2, (p!.a[1] + p!.b[1]) / 2];
  }
  // fără document (un nivel desenat fără soluție) rămân felul și lungimea
  const grupuri: Group[] = det
    ? det.grupuri.map((gr) => ({
        label: t(`detalii.grup.${gr.cheie}` as Cheie),
        rows: gr.randuri.map((x) => ({ k: tr(`detalii.${x.cheie}`, x.cheie), v: x.valoare })),
      }))
    : [{ label: t("insp.grup.forma"), rows: r
        ? [{ k: t("insp.rand.lungime"), v: m(r.axis.slice(1).reduce((s, q, i) =>
              s + Math.hypot(q[0] - r.axis[i][0], q[1] - r.axis[i][1]), 0)) },
           { k: t("insp.rand.inaltime"), v: m(r.height_m) }]
        : [{ k: t("insp.rand.lungime"), v: m(Math.hypot(p!.b[0] - p!.a[0], p!.b[1] - p!.a[1])) },
           { k: t("insp.rand.inaltime"), v: m(p!.z1 - p!.z0) }] }];
  const titlu = det?.titlu ?? fel;
  return {
    title: titlu.charAt(0).toUpperCase() + titlu.slice(1),
    chips: [fel, nivel.label],
    at,
    figure: { poly: figura },
    groups: [...grupuri,
             { label: t("insp.grup.coordonate"), rows: [{ k: t("insp.rand.centru"), v: xy(at[0], at[1]) }] }],
    rules: [],
  };
}

/** Turn a selection into printable rows. Returns null when the selection points
 *  outside the level currently loaded — which happens legitimately, because the
 *  streaming solve swaps the plan under a held selection. */
export function describeSelection(sel: Selection, ctx: DescribeCtx): Described | null {
  // Normele și numele lor, în limba de ACUM: `reguli()` citește catalogul la
  // fiecare chemare, iar panoul se redesenează la schimbarea limbii.
  const R = reguli();
  /** Eticheta nivelului pe chip: «nivel -1» sub pământ, «etaj 3» deasupra. */
  const nivelChip = (n: number | string) => t("insp.chip.nivel", { n: String(n) });
  const etajChip = (n: number | string) => t("insp.chip.etaj", { n: String(n) });
  switch (sel.kind) {
    case "storey": {
      // Nothing to outline and nothing to measure: the inspector's job for a
      // storey selection is to say which floor it is. The validator's row carries
      // the finding; navigating to it switches the level, which is the whole
      // point of the selection.
      return {
        title: sel.level === "site" ? t("insp.nivel.situatie")
             : sel.level === 0 ? t("insp.nivel.parter")
             : (sel.level as number) < 0
               ? t("insp.nivel.subsol", { n: String(sel.level) })
               : t("insp.nivel.etaj", { n: String(sel.level) }),
        chips: [t("insp.chip.nivel_intreg")],
        groups: [],
        rules: [],
      };
    }

    case "cota":
      // doar pe desen — v. `Selection`
      return null;

    case "obiect": {
      const g = gasesteObiect(ctx.storey ?? null, sel.eid);
      if (!g) {
        const f = gasestePiesaFatadei(ctx.storey ?? null, sel.eid);
        return f ? descriePiesaFatadei(f, sel.eid, ctx.doc) : null;
      }
      const { o, familie, nivel } = g;
      // Numele kind-ului vine din catalogul generat, ca peste tot: „Perete de
      // fațadă", „Compartimentare", „Parapet", „Placă în consolă". Când
      // obiectul n-are kind (o fereastră, un stâlp), rămâne numele familiei.
      const numeKind = o.kind ? (numeleFelului(o.kind) || null) : null;
      const rows: Row[] = [];
      if (o.code) rows.push({ k: t("insp.rand.cod"), v: o.code });
      // …dar nu ca RÂND când e deja titlul.
      if (o.label && o.label !== numeKind) { /* e titlul */ }
      if (numeKind) rows.push({ k: t("insp.rand.tip"), v: numeKind });
      // REZISTENȚA LA FOC e a obiectului, scrisă de generatorul care a făcut
      // peretele («EI 120» pe holul SAS, «EI 120» / «EI 180» pe nucleu și pe
      // compartimentare — v. `rezistentaLaFoc`). Se arată ce e scris, nu o
      // cifră a interfeței: un perete fără rezistență scrisă n-are rândul.
      if (o.rezistenta) rows.push({ k: t("insp.rand.rezistenta_foc"), v: o.rezistenta });
      const forma: Row[] = [];
      if (o.thickness_m) forma.push({ k: t("insp.rand.grosime"), v: m(o.thickness_m) });
      if (o.width_m) forma.push({ k: t("insp.rand.latime"), v: m(o.width_m) });
      if (o.height_m) forma.push({ k: t("insp.rand.inaltime"), v: m(o.height_m) });
      if (o.axis && o.axis.length === 2) {
        forma.push({ k: t("insp.rand.lungime_axa"),
                     v: m(Math.hypot(o.axis[1][0] - o.axis[0][0],
                                     o.axis[1][1] - o.axis[0][1])) });
      }
      // …CU unghiul obiectului. Era singurul apel din fișier care îl uita, deși
      // `shapeOf` spune în comentariul ei că măsoară în cadrul propriu: o cameră
      // pe o bară oblică primea gabaritul cutiei aliniate la axele lumii.
      forma.push(...shapeOf(o.poly, o.deg ?? 0));
      const [cx, cy] = o.cx != null && o.cy != null
        ? [o.cx, o.cy] : centroid(o.poly.length ? o.poly : [[0, 0]]);
      return {
        // TITLUL e ce a scris arhitectul, când a scris ceva: „Dormitor 1",
        // „Living + bucătărie". Numele familiei („Spațiu", „Perete") e corect,
        // dar e clasa obiectului, nu obiectul — iar un panou care titrează
        // „Spațiu" peste o cameră care își poartă numele pe desen spune mai
        // puțin decât desenul. Familia rămâne pe chip, unde e la locul ei.
        title: o.label || numeKind || numeleFamiliei(familie),
        chips: [numeKind ?? numeleFamiliei(familie),
                ...(o.rezistenta ? [o.rezistenta] : []), nivel.label],
        at: [cx, cy],
        // FIGURA ARATĂ CAMERA ÎN APARTAMENTUL EI, nu singură pe fond alb. Un
        // contur pe cont propriu nu spune de care capăt al locuinței ții — și
        // asta cerea utilizatoarea. Legătura e pe ID (`Space.properties.flat`,
        // purtat până în `StoreyShape.flat`), nu ghicită din geometrie: o
        // potrivire prin „centrul camerei cade în conturul ăsta" e o euristică,
        // și pe camere în L cade lângă. Fără `flat` scris în document — un nivel
        // importat din DWG n-are obiecte `Flat` — rămâne figura simplă.
        figure: o.poly.length >= 3
          ? { poly: o.poly, context: contextulCamerei(o, nivel) }
          : undefined,
        groups: [
          ...(rows.length ? [{ label: t("insp.grup.identitate"), rows }] : []),
          { label: t("insp.grup.forma"), rows: forma },
          { label: t("insp.grup.coordonate"),
            rows: [{ k: t("insp.rand.centru"), v: xy(cx, cy) }] },
          // DE CE EXISTĂ SCARA ASTA, când limita de evacuare a cumpărat-o.
          //
          // Numai pe scările suplimentare de garaj: solverul le așază DELIBERAT
          // în afara amprentei corpurilor, deci pe planșă apare o casă de scară
          // sub care nu stă nicio clădire, iar prima întrebare a oricui o vede e
          // «de ce e aici?». Rândurile spun punctul, cât se mergea până la o
          // ieșire cu scările care existau deja, și limita în vigoare atunci —
          // deci afirmația se poate verifica, nu doar crede.
          //
          // Fără câmp nu se adaugă nimic: scara e nucleul clădirii de deasupra,
          // care are nevoie de acces în subsol oricum.
          ...evacuareGrup(o.evacDriver),
          ...alaGrup(o.ala),
        ],
        rules: [],
      };
    }

    case "stall": {
      const s = sel.level === "site"
        ? ctx.site?.stalls?.[sel.i] : ctx.plan?.spaces?.[sel.i];
      if (!s) return null;
      const ada = s.kind === "ada";
      // A basement bay carries its own size, read off the document (5,00 m deep
      // perpendicular, 5,75 m along the aisle when parallel); a surface stall
      // arrives untranslated with only a centre, so it falls back to the
      // constants its glyph is drawn with. The Formă rows below print the same
      // numbers the outline is drawn with — one source for both.
      const size = s as { w?: number; d?: number };
      const sw = size.w ?? 2.5, sd = size.d ?? 5.0;
      // Zecimala se scrie în limba omului — virgulă la noi, punct în engleză
      // —, deci prin `numar`, nu printr-un `replace` pe punct.
      const dim = (v: number) =>
        numar(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const route = sel.level !== "site"
        ? ctx.plan?.evac?.routes?.[sel.i] ?? null : null;
      const rules = [R.stallSize, R.aisleWidth];
      if (ada) rules.push(R.adaCount, R.adaStrip, R.adaLevel);
      if (route) rules.push(R.evacTwoWay);
      return {
        title: t(ada ? "insp.titlu.loc_adaptat" : "insp.titlu.loc_parcare"),
        chips: [sel.level === "site" ? t("insp.chip.la_sol") : nivelChip(sel.level),
                ...(ada ? [t("insp.chip.dizabilitati")] : [])],
        at: [s.cx, s.cy],
        figure: { poly: rectPoly(s.cx, s.cy, sw, sd, s.deg ?? 0) },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.numar"), v: `${sel.i + 1}` },
            { k: t("insp.rand.tip"),
              v: ada ? t("insp.stall.adaptat", { w: dim(sw), d: dim(sd) })
                     : t("insp.stall.standard") },
          ] },
          { label: t("insp.grup.forma"), rows: [
            { k: t("insp.rand.dimensiuni"),
              v: t("insp.stall.dimensiuni", { w: dim(sw), d: dim(sd) }) },
            { k: t("insp.rand.arie"), v: m2(sw * sd) },
            { k: t("insp.rand.orientare"),
              v: grade((((s.deg ?? 0) % 360) + 360) % 360) },
          ] },
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.centru"), v: xy(s.cx, s.cy) },
          ] },
          ...(route ? [{ label: t("insp.grup.evacuare"), rows: [
            { k: t("insp.evac.traseu_sas"), v: m(route.len) },
            { k: t("insp.evac.limita_scurt"),
              v: t("insp.evac.doua_directii", { n: EVAC_TWO_DIR_M }),
              warn: route.len > EVAC_TWO_DIR_M },
          ] }] : []),
        ],
        rules,
      };
    }

    case "adaAisle": {
      const a: AdaAisle | undefined = ctx.plan?.adaAisles?.[sel.i];
      if (!a) return null;
      return {
        title: t("insp.titlu.fasie_ada"),
        chips: [nivelChip(sel.level), t("insp.chip.nu_se_parcheaza")],
        at: [a.cx, a.cy],
        groups: [
          { label: t("insp.grup.forma"), rows: [
            { k: t("insp.rand.dimensiuni"), v: `${m(a.h)} × ${m(a.w)}` },
            { k: t("insp.rand.arie"), v: m2(a.w * a.h) },
            { k: t("insp.rand.orientare"), v: grade(a.deg) },
          ] },
          { label: t("insp.grup.coordonate"),
            rows: [{ k: t("insp.rand.centru"), v: xy(a.cx, a.cy) }] },
        ],
        rules: [R.adaStrip, R.adaCount],
      };
    }

    case "lane": {
      const l = ctx.plan?.lanes?.[sel.i];
      if (!l) return null;
      // Aleea e în document o axă cu o lățime; ce se vede pe planșă e
      // dreptunghiul din jurul ei. Aici se citește identitatea ei: de unde
      // vine, cât e de lungă, pe unde trece (Oana, 18.09.2026).
      const lung = Math.hypot(l.x2 - l.x1, l.y2 - l.y1);
      const lat = l.w ?? 5;
      const dublu = lat >= 5.5;
      return {
        title: t("insp.titlu.alee"),
        chips: [nivelChip(sel.level),
                t(dublu ? "insp.chip.dublu_sens" : "insp.chip.sens_unic")],
        at: [(l.x1 + l.x2) / 2, (l.y1 + l.y2) / 2],
        figure: { poly: [[l.x1, l.y1], [l.x2, l.y2]] },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.denumire"), v: l.sursa ?? `#${sel.i + 1}` },
          ] },
          { label: t("insp.grup.forma"), rows: [
            { k: t("insp.rand.lungime"), v: m(lung) },
            { k: t("insp.rand.latime"), v: m(lat) },
            { k: t("insp.rand.arie"), v: m2(lung * lat) },
          ] },
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.de_la"), v: xy(l.x1, l.y1) },
            { k: t("insp.rand.pana_la"), v: xy(l.x2, l.y2) },
          ] },
        ],
        rules: [R.aisleWidth],
      };
    }

    case "column": {
      const c: Column | undefined = ctx.columns?.[sel.i];
      if (!c) return null;
      // A blade longer than it is thick is a lamella carrying a wall above; a
      // near-square one is a stall-strip column on the parking's own module.
      const long = Math.max(c.w, c.h), thin = Math.min(c.w, c.h);
      const lamella = long / Math.max(thin, 1e-6) > 2.5;
      return {
        title: t(lamella ? "insp.titlu.lamela" : "insp.titlu.stalp"),
        chips: [nivelChip(sel.level),
                t(lamella ? "insp.chip.sub_perete" : "insp.chip.modul_parcare")],
        at: [c.cx, c.cy],
        figure: { poly: rectPoly(c.cx, c.cy, c.w, c.h, c.deg) },
        groups: [
          { label: t("insp.grup.forma"), rows: [
            { k: t("insp.rand.sectiune"), v: `${m(c.w)} × ${m(c.h)}` },
            { k: t("insp.rand.arie"), v: m2(c.w * c.h) },
            { k: t("insp.rand.orientare"), v: grade(c.deg) },
          ] },
          { label: t("insp.grup.coordonate"),
            rows: [{ k: t("insp.rand.centru"), v: xy(c.cx, c.cy) }] },
        ],
        rules: [R.column],
      };
    }

    case "room": {
      const r: SolverRoom | undefined = ctx.plan?.rooms?.[sel.i];
      if (!r) return null;
      const rules = r.kind === "scara" ? [R.coreEnclosure, R.evacTwoWay]
                  : r.kind === "sas" ? [R.fireDoor] : [];
      return {
        title: numeleFelului(r.kind) || r.label,
        chips: [nivelChip(sel.level), r.kind],
        at: [r.cx, r.cy],
        figure: { poly: rectPoly(r.cx, r.cy, r.w, r.h, r.deg) },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.denumire"), v: r.label },
            { k: t("insp.rand.arie_declarata"), v: m2(r.area) },
          ] },
          { label: t("insp.grup.forma"), rows: [
            { k: t("insp.rand.dimensiuni"), v: `${m(r.w)} × ${m(r.h)}` },
            { k: t("insp.rand.arie_geometrica"), v: m2(r.w * r.h) },
            { k: t("insp.rand.orientare"), v: grade(r.deg) },
          ] },
          { label: t("insp.grup.coordonate"),
            rows: [{ k: t("insp.rand.centru"), v: xy(r.cx, r.cy) }] },
        ],
        rules,
      };
    }

    case "fireWall": {
      const w: FireWall | undefined = ctx.fireWalls?.[sel.i];
      if (!w) return null;
      const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
      return {
        title: t("insp.titlu.perete_antifoc"),
        chips: [nivelChip(sel.level), "EI 120"],
        at: [(w.x1 + w.x2) / 2, (w.y1 + w.y2) / 2],
        groups: [
          { label: t("insp.grup.forma"),
            rows: [{ k: t("insp.rand.lungime"), v: m(len) }] },
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.de_la"), v: xy(w.x1, w.y1) },
            { k: t("insp.rand.pana_la"), v: xy(w.x2, w.y2) },
          ] },
        ],
        rules: [R.fireWall, R.fireDoor],
      };
    }

    case "fireDoor": {
      const d: FireDoor | undefined = ctx.fireDoors?.[sel.i];
      if (!d) return null;
      return {
        title: t("insp.titlu.usa_antifoc"),
        chips: [nivelChip(sel.level), t("insp.chip.pe_traversare")],
        at: [d.x, d.y],
        groups: [
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.centru"), v: xy(d.x, d.y) },
            { k: t("insp.rand.orientare"), v: grade(d.angleDeg) },
          ] },
        ],
        rules: [R.fireDoor, R.fireWall],
      };
    }

    case "rampInt": {
      // cu două accese sunt două rampe spre -2; `rampsInt` le are pe toate,
      // `rampInt` rămâne prima, pentru documentele dinainte
      const r = ctx.plan?.rampsInt?.[sel.i] ?? (sel.i === 0 ? ctx.plan?.rampInt : null);
      if (!r) return null;
      return {
        title: t("insp.titlu.rampa"),
        chips: [nivelChip(sel.level)],
        at: [r.cx, r.cy],
        figure: { poly: rectPoly(r.cx, r.cy, r.run, r.w, r.deg) },
        groups: [
          { label: t("insp.grup.forma"), rows: [
            { k: t("insp.rand.lungime_desfasurata"), v: m(r.run) },
            { k: t("insp.rand.latime"), v: m(r.w) },
            { k: t("insp.rand.orientare"), v: grade(r.deg) },
          ] },
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.sus"), v: xy(r.top[0], r.top[1]) },
            { k: t("insp.rand.jos"), v: xy(r.bottom[0], r.bottom[1]) },
          ] },
        ],
        rules: [R.rampSlope],
      };
    }

    // ---- floors above ground ---------------------------------------------

    case "flat": {
      const f: ApartmentFlat | undefined = ctx.apt?.flats?.[sel.i];
      if (!f) return null;
      const deg = f.deg ?? 0;
      const need = f.useful_required;
      const wrap = f.wraps_core;
      const habitable = f.layout.filter(
        (r) => r.kind === "living" || r.kind === "bedroom");
      return {
        title: f.label,
        chips: [etajChip(sel.level), t("insp.chip.persoane", { n: String(f.persons) }),
                ...(wrap ? [t("insp.chip.intoarsa")] : []),
                ...(f.building ? [f.building] : [])],
        at: centroid(f.poly),
        figure: { poly: f.poly,
                  context: f.layout.map((rm) => rm.poly as number[][]) },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.tip"),
              v: t("insp.flat.tip",
                   { camere: String(f.rooms), persoane: String(f.persons) }) },
            { k: t("insp.rand.utila"), v: m2(f.area) },
            ...(need !== undefined ? [{
              k: t("insp.rand.minim_legal"), v: m2(need),
              warn: f.area < need - 0.01,
            }] : []),
            ...(f.frontage !== undefined
              ? [{ k: t("insp.rand.front"), v: m(f.frontage) }] : []),
            ...(f.balcony
              ? [{ k: t("insp.rand.balcon_separat"), v: m2(f.balcony.area) }] : []),
          ] },
          { label: t("insp.grup.forma"), rows: [
            ...shapeOf(f.poly, deg),
            { k: t("insp.rand.camere_desenate"), v: `${f.layout.length}` },
            { k: t("insp.rand.camere_locuibile"),
              v: t("insp.flat.toate_pe_fatada", { n: habitable.length }) },
          ] },
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.centru"), v: xy(...centroid(f.poly)) },
          ] },
        ],
        rules: [R.usefulArea, R.insolation, R.sanitary,
                R.hallWidth, R.evacFlat,
                ...(f.balcony ? [R.balcony] : [])],
      };
    }

    case "flatRoom": {
      const f: ApartmentFlat | undefined = ctx.apt?.flats?.[sel.i];
      const r: ApartmentRoom | undefined = f?.layout?.[sel.j];
      if (!f || !r) return null;
      const rule = roomRule(R)[r.kind];
      const poly = r.poly as number[][];
      return {
        title: r.label,
        chips: [etajChip(sel.level), f.label, r.kind],
        at: centroid(poly),
        figure: {
          poly,
          context: [f.poly as number[][],
                    ...f.layout.filter((_, k) => k !== sel.j)
                       .map((rm) => rm.poly as number[][])],
        },
        parent: { label: t("insp.flat.utili",
                           { eticheta: f.label, arie: m2(f.area) }),
                  sel: { kind: "flat", level: sel.level, i: sel.i } },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.in_locuinta"),
              v: t("insp.flat.utili", { eticheta: f.label, arie: m2(f.area) }) },
            ...(r.area !== undefined
              ? [{ k: t("insp.rand.arie"), v: m2(r.area) }] : []),
            ...(r.min_area !== undefined ? [{
              k: t("insp.rand.minim_legal"), v: m2(r.min_area),
              warn: r.area !== undefined && r.area < r.min_area - 0.01,
            }] : []),
          ] },
          { label: t("insp.grup.forma"), rows: shapeOf(poly, f.deg ?? 0) },
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.centru"), v: xy(...centroid(poly)) },
          ] },
        ],
        rules: rule ? [rule, R.insolation] : [R.insolation],
      };
    }

    case "balcony": {
      const f: ApartmentFlat | undefined = ctx.apt?.flats?.[sel.i];
      if (!f?.balcony) return null;
      return {
        title: t("insp.titlu.balcon"),
        chips: [etajChip(sel.level), f.label],
        at: centroid(f.balcony.poly),
        figure: { poly: f.balcony.poly, context: [f.poly as number[][]] },
        parent: { label: t("insp.flat.utili",
                           { eticheta: f.label, arie: m2(f.area) }),
                  sel: { kind: "flat", level: sel.level, i: sel.i } },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.arie"), v: m2(f.balcony.area) },
            { k: t("insp.rand.al_locuintei"),
              v: t("insp.flat.utili_um",
                   { eticheta: f.label, arie: m2(f.area) }) },
          ] },
          { label: t("insp.grup.forma"), rows: shapeOf(f.balcony.poly, f.deg ?? 0) },
        ],
        rules: [R.balcony],
      };
    }

    case "corePart": {
      const c = ctx.apt?.cores?.[sel.i];
      const p: ApartmentCorePart | undefined = c?.parts?.[sel.j];
      if (!c || !p) return null;
      return {
        title: numeleFelului(p.kind) || p.label,
        chips: [etajChip(sel.level), p.kind,
                t(c.shared ? "insp.chip.comuna_parcaj" : "insp.chip.proprie")],
        at: (p.cx !== undefined && p.cy !== undefined
          ? [p.cx, p.cy] : centroid(p.poly)) as [number, number],
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.denumire"), v: p.label },
            ...(p.area !== undefined
              ? [{ k: t("insp.rand.arie"), v: m2(p.area) }] : []),
            { k: t("insp.rand.nucleu"),
              v: t("insp.rand.nucleu_centru", { xy: xy(c.cx, c.cy) }) },
          ] },
          { label: t("insp.grup.forma"), rows: [
            ...(p.w !== undefined && p.h !== undefined
              ? [{ k: t("insp.rand.dimensiuni"), v: `${m(p.w)} × ${m(p.h)}` }] : []),
            ...shapeOf(p.poly, p.deg ?? 0),
          ] },
        ],
        rules: p.kind === "scara" ? [R.coreEnclosure, R.evacFlat]
             : p.kind === "hol" ? [R.corridorWidth] : [],
      };
    }

    case "bar":
    case "corridor": {
      const b = ctx.apt?.bars?.[sel.i];
      if (!b) return null;
      const poly = sel.kind === "bar" ? b.poly : b.corridor;
      return {
        title: t(sel.kind === "bar" ? "insp.titlu.tronson" : "insp.titlu.coridor"),
        chips: [etajChip(sel.level)],
        at: centroid(poly),
        figure: { poly, context: [sel.kind === "corridor" ? b.poly : b.corridor] },
        groups: [
          { label: t("insp.grup.forma"), rows: shapeOf(poly) },
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.centru"), v: xy(...centroid(poly)) },
          ] },
        ],
        rules: sel.kind === "corridor"
          ? [R.corridorWidth, R.evacFlat] : [R.insolation],
      };
    }

    case "building": {
      const b = ctx.apt?.buildings?.[sel.i];
      if (!b) return null;
      return {
        title: t("insp.titlu.cladire", { id: b.id }),
        chips: [etajChip(sel.level)],
        at: centroid(b.poly),
        figure: { poly: b.poly },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.ap_pe_nivel"), v: `${b.flats}` },
            { k: t("insp.rand.nuclee"), v: `${b.cores}` },
            { k: t("insp.rand.utila_nivel"), v: m2(b.useful_m2) },
            { k: t("insp.rand.amprenta"), v: m2(b.footprint_m2) },
          ] },
          { label: t("insp.grup.forma"), rows: shapeOf(b.poly) },
        ],
        rules: [R.insolation, R.evacFlat],
      };
    }

    // ---- the site layer ---------------------------------------------------

    case "siteBar": {
      const b = ctx.site?.bars?.[sel.i];
      if (!b) return null;
      return {
        title: t("insp.titlu.corp"),
        chips: [t("insp.chip.situatie"), b.label],
        at: centroid(b.pts),
        figure: { poly: b.pts },
        groups: [
          { label: t("insp.grup.identitate"), rows: [
            { k: t("insp.rand.regim"), v: b.label },
            { k: t("insp.rand.cornisa"), v: m(b.h) },
          ] },
          { label: t("insp.grup.forma"), rows: shapeOf(b.pts) },
        ],
        rules: [R.insolation],
      };
    }

    case "siteRoad": {
      const r = ctx.site?.roads?.[sel.i];
      if (!r || Array.isArray(r)) return null;
      const poly = (r.poly ?? []) as number[][];
      const p1 = r.p1 ?? poly[0] ?? [0, 0], p2 = r.p2 ?? poly[1] ?? p1;
      const lung = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
      const rows = [
        // piesa motorului («T3») sau id-ul agentului («N3»): un singur rând,
        // nu pot fi amândouă pe același drum
        { k: t("insp.rand.eticheta"), v: r.eticheta ?? r.agent ?? "—" },
        { k: t("insp.rand.fel"), v: r.fel ?? r.kind ?? "—" },
        // sensul vine din document (`two_way`); lipsa lui înseamnă dublu sens,
        // ca la desen (`roadAxis`)
        { k: t("insp.rand.sens"), v: t(r.two_way === false ? "insp.sens.unic" : "insp.sens.dublu") },
        { k: t("insp.rand.latime"), v: m(r.w ?? 0) },
        { k: t("insp.rand.lungime_axa"), v: m(lung) },
      ];
      if (r.nota) rows.push({ k: t("insp.rand.nota"), v: r.nota });
      return {
        title: t("insp.titlu.alee"),
        chips: [t("insp.chip.situatie"), ...(r.eticheta ?? r.agent ? [r.eticheta ?? r.agent!] : [])],
        at: [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2],
        figure: poly.length >= 3 ? { poly } : undefined,
        groups: [{ label: t("insp.grup.identitate"), rows }],
        rules: [],
      };
    }

    case "siteSidewalk": {
      const sh = ctx.site?.sidewalks?.[sel.i];
      if (!sh) return null;
      const poly = Array.isArray(sh) ? sh : sh.poly;
      const o: { agent?: string; w?: number; axis?: number[][] } = Array.isArray(sh) ? {} : sh;
      const rows = [
        { k: t("insp.rand.fel"), v: t("insp.fel.trotuar") },
        ...(o.agent ? [{ k: t("insp.rand.id_agent"), v: o.agent }] : []),
        ...(o.w ? [{ k: t("insp.rand.latime"), v: m(o.w) }] : []),
        ...(o.axis && o.axis.length >= 2 ? [{ k: t("insp.rand.lungime_axa"), v: m(lungimeAxei(o.axis)) }] : []),
      ];
      return {
        title: t("insp.titlu.trotuar"),
        chips: [t("insp.chip.situatie"), ...(o.agent ? [o.agent] : [])],
        at: centroid(poly),
        figure: poly.length >= 3 ? { poly } : undefined,
        groups: [{ label: t("insp.grup.identitate"), rows }],
        rules: [],
      };
    }

    case "siteCrossing": {
      const c = ctx.site?.crossings?.[sel.i];
      if (!c) return null;
      const rows = [
        { k: t("insp.rand.fel"), v: t("insp.fel.trecere") },
        ...(c.agent ? [{ k: t("insp.rand.id_agent"), v: c.agent }] : []),
        ...(c.alee ? [{ k: t("insp.rand.alee_traversata"), v: c.alee }] : []),
        ...(c.w ? [{ k: t("insp.rand.latime"), v: m(c.w) }] : []),
        ...(c.axis && c.axis.length >= 2 ? [{ k: t("insp.rand.lungime_axa"), v: m(lungimeAxei(c.axis)) }] : []),
      ];
      return {
        title: t("insp.titlu.trecere"),
        chips: [t("insp.chip.situatie"), ...(c.agent ? [c.agent] : [])],
        at: centroid(c.poly),
        figure: { poly: c.poly },
        groups: [{ label: t("insp.grup.identitate"), rows }],
        rules: [],
      };
    }

    case "siteZone": {
      const z = ctx.site?.zones?.[sel.i];
      if (!z) return null;
      const rows = [
        // felul zonei, cu eticheta catalogului («loc de joacă»)
        { k: t("insp.rand.fel"), v: `${t("insp.fel.zona")} · ${z.label}` },
        ...(z.agent ? [{ k: t("insp.rand.id_agent"), v: z.agent }] : []),
      ];
      return {
        title: t("insp.titlu.zona"),
        chips: [t("insp.chip.situatie"), z.label, ...(z.agent ? [z.agent] : [])],
        at: centroid(z.poly),
        figure: { poly: z.poly },
        groups: [
          { label: t("insp.grup.identitate"), rows },
          { label: t("insp.grup.forma"), rows: shapeOf(z.poly) },
        ],
        rules: [],
      };
    }

    case "siteStair": {
      const s = ctx.site?.scariEvacuare?.[sel.i];
      if (!s) return null;
      const c = s.contur;
      const cx = c.reduce((a, p) => a + p[0], 0) / c.length, cy = c.reduce((a, p) => a + p[1], 0) / c.length;
      const rows = [
        { k: t("insp.rand.eticheta"), v: s.id },
        { k: t("insp.rand.niveluri"), v: (s.niveluri ?? []).map((n) => String(n)).join(", ") || "—" },
        { k: t("insp.rand.tipar"), v: s.tipar ?? "—" },
        { k: t("insp.rand.rezistenta"), v: s.rezistenta ?? "—" },
        { k: t("insp.rand.persoane"), v: s.persoane != null ? `${s.persoane} · ${s.fluxuri ?? 1} × ${m(s.latimeFluxM)}` : "—" },
        { k: t("insp.rand.zona_libera"), v: m(s.zonaM) },
      ];
      return {
        title: t("insp.titlu.scara_evacuare"),
        chips: [t("insp.chip.situatie"), s.id],
        at: [cx, cy],
        figure: { poly: c },
        groups: [{ label: t("insp.grup.identitate"), rows }],
        rules: [],
      };
    }

    case "siteEntrance": {
      const e = ctx.site?.entrances?.[sel.i];
      if (!e) return null;
      return {
        title: t("insp.titlu.acces"),
        chips: [t("insp.chip.situatie")],
        at: [e.x, e.y],
        groups: [
          { label: t("insp.grup.coordonate"), rows: [
            { k: t("insp.rand.usa"), v: xy(e.x, e.y) },
            { k: t("insp.rand.orientare"), v: grade(e.deg) },
          ] },
        ],
        rules: [R.evacFlat],
      };
    }

    case "plotEdge": {
      // Found by `side_index`, not by array position: the ref carries the side's
      // own number so it survives the objects being reordered.
      const e = (ctx.site?.edges ?? []).find((x) => x.i === sel.i);
      if (!e) return null;
      const ax = e.axis ?? [];
      const len = ax.length >= 2
        ? Math.hypot(ax[1][0] - ax[0][0], ax[1][1] - ax[0][1]) : 0;
      // Umbrește `m`-ul de modul doar ca să scrie „nedeclarat" pe o retragere
      // lipsă; separatorul zecimal e acum al limbii, în amândouă.
      const m = (v: number | null | undefined) =>
        (v == null ? t("insp.nedeclarat") : `${numar(v, doua)} m`);
      // Both setbacks, and which one governs — the larger, because they are two
      // independent requirements on the same distance and both hold.
      const gov = e.setback_urbanism_m != null && e.setback_fire_m != null
        ? t(e.setback_urbanism_m >= e.setback_fire_m
            ? "insp.gov.urbanism" : "insp.gov.incendiu")
        : "";
      return {
        title: e.name || t("insp.titlu.latura", { n: sel.i }),
        chips: [t("insp.chip.limita_parcela"),
                t(e.kind === "street"
                  ? "insp.chip.spre_strada" : "insp.chip.spre_vecin"),
                ...(e.urbanism_is_assumed
                  ? [t("insp.chip.retragere_presupusa")] : [])],
        at: ax.length >= 2
          ? [(ax[0][0] + ax[1][0]) / 2, (ax[0][1] + ax[1][1]) / 2] : undefined,
        groups: [
          { label: t("insp.grup.retrageri"), rows: [
            { k: t("insp.rand.urbanism"), v: m(e.setback_urbanism_m) },
            { k: t("insp.rand.incendiu"), v: m(e.setback_fire_m) },
            ...(gov ? [{ k: t("insp.rand.guverneaza"), v: gov }] : []),
            { k: t("insp.rand.aliniere"), v: m(e.alignment_distance_m) },
          ] },
          { label: t("insp.grup.geometrie"),
            rows: [{ k: t("insp.rand.lungime"), v: `${numar(len, doua)} m` }] },
        ],
        rules: [],
      };
    }
  }
  return null;
}
